// src/context/NavContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  query,
  onSnapshot,
} from "firebase/firestore";
import moment from "moment";
import { useAuth } from "../context/AuthContext";

const NavContext = createContext();

export const NavProvider = ({ children }) => {
  const { user, orgId } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [proximasAVencer, setProximasAVencer] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);

  useEffect(() => {
    if (!user || !orgId) return;

    const db = getFirestore();
    const unsubs = [];

    /* ────── Cotizaciones (orgs/{orgId}/cotizaciones) ────── */
    const cotRef = collection(db, `orgs/${orgId}/cotizaciones`);
    unsubs.push(
      onSnapshot(
        cotRef,
        (snap) => {
          const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const prox = data
            .filter((c) =>
              moment(c.fechaVencimiento).isSameOrAfter(moment().startOf("day")),
            )
            .sort((a, b) =>
              moment(a.fechaVencimiento).diff(moment(b.fechaVencimiento)),
            ); // sin slice
          setProximasAVencer(prox);
        },
        (err) => console.error("cotizaciones error:", err),
      ),
    );

    /* ────── Feeds calendario dentro de la ORG ────── */
    const feeds = [
      { col: "eventos", tipo: "evento", date: "to" },
      { col: "tareaCalendario", tipo: "tarea", date: "to" },
      { col: "trabajoCalendario", tipo: "trabajo", date: "to" },
    ];

    feeds.forEach(({ col, tipo, date }) => {
      const qRef = query(collection(db, `orgs/${orgId}/${col}`));
      const unsub = onSnapshot(
        qRef,
        (snap) => mergeEventos(snap.docs, { tipo, date }),
        (err) => console.error(`${tipo} error:`, err),
      );
      unsubs.push(unsub);
    });

    function mergeEventos(docs, { tipo, date }) {
      const parsed = docs
        .map((d) => {
          const data = d.data();
          const raw = dateFromDoc(data[date]);
          return raw
            ? {
                id: `${tipo}-${d.id}`,
                title: data.title ?? "(sin título)",
                to: raw,
                resource: tipo,
              }
            : null;
        })
        .filter(Boolean);

      setProximosEventos((prev) => {
        const sinEsteTipo = prev.filter((e) => e.resource !== tipo);

        const prox = parsed
          .filter((e) => moment(e.to).isSameOrAfter(moment().startOf("day")))
          .sort((a, b) => moment(a.to).diff(moment(b.to))); // sin slice

        return [...sinEsteTipo, ...prox];
      });
    }

    function dateFromDoc(v) {
      if (!v) return null;
      if (v?.seconds) return v.toDate(); // Timestamp
      if (v instanceof Date) return v;
      return new Date(v); // string ISO
    }

    return () => unsubs.forEach((fn) => fn());
  }, [user, orgId]);

  return (
    <NavContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        proximasAVencer,
        proximosEventos,
      }}
    >
      {children}
    </NavContext.Provider>
  );
};

export const useNavContext = () => useContext(NavContext);
